/* ExcelImportModal.js */
import React, { useState, useRef, useCallback } from 'react';
import JalaliDatePicker from '../../components/JalaliDatePicker';

const toFa = n => n===null||n===undefined?'':String(n).replace(/[0-9]/g,d=>'۰۱۲۳۴۵۶۷۸۹'[d]);
const numFmt = n => Number(n||0).toLocaleString('fa-IR');

/* نگاشت پیش‌فرض ستون‌ها */
const BANK_FIELDS = [
  {key:'amount_idx',   label:'مبلغ درآمد *', def:2},
  {key:'shenase_idx',  label:'شناسه واریز',  def:0},
  {key:'tracking_idx', label:'کد پیگیری',    def:0},
  {key:'transno_idx',  label:'شماره تراکنش', def:5},
  {key:'date_idx',     label:'تاریخ تراکنش', def:12},
  {key:'time_idx',     label:'زمان تراکنش',  def:12},
  {key:'remark_idx',   label:'توضیح',        def:0},
];
const SAMP_FIELDS = [
  {key:'dept_code_idx',    label:'کد واحد *',    def:1},
  {key:'dept_name_idx',    label:'عنوان واحد',   def:2},
  {key:'shenase_idx',      label:'شناسه دریافت', def:11},
  {key:'file_no_idx',      label:'شماره پرونده', def:14},
  {key:'income_no_idx',    label:'کد درآمد',     def:0},
  {key:'income_title_idx', label:'نوع درآمد',    def:8},
  {key:'samp_date_idx',    label:'تاریخ سمپ',    def:5},
  {key:'samp_amount_idx',  label:'مبلغ سمپ *',   def:20},
];
const SCALES = [
  {val:1,       label:'مبالغ به ریال'},
  {val:1000,    label:'مبالغ به هزار ریال'},
  {val:1000000, label:'مبالغ به میلیون ریال'},
];

const authHeader = () => ({Authorization:`Bearer ${localStorage.getItem('access')}`});
const curYear = () => {
  const y = new Date().toLocaleDateString('fa-IR-u-nu-latn').split('/')[0];
  return String(y).replace(/[۰-۹]/g,d=>'۰۱۲۳۴۵۶۷۸۹'.indexOf(d));
};

export default function ExcelImportModal({ kind, onClose, onDone }) {
  const isBank  = kind === 'bank';
  const FIELDS  = isBank ? BANK_FIELDS : SAMP_FIELDS;
  const storeKey = isBank ? 'arjan_GetIncomeExcellColumns' : 'arjan_GetSampExcellColumns';

  /* نگاشت ذخیره‌شده کاربر */
  const loadSaved = () => {
    try {
      const raw = JSON.parse(localStorage.getItem(storeKey) || 'null');
      if (raw) return raw;
    } catch (e) { /* مقدار خراب — از پیش‌فرض استفاده می‌شود */ }
    return FIELDS.reduce((a,f)=>({...a,[f.key]:f.def}),{});
  };

  const [file,setFile]       = useState(null);
  const [sheets,setSheets]   = useState([]);
  const [sheet,setSheet]     = useState('');
  const [preview,setPreview] = useState([]);
  const [colCount,setColCount]= useState(0);
  const [totalRows,setTotalRows]= useState(0);
  const [map,setMap]         = useState(loadSaved);
  const [armed,setArmed]     = useState(null);   // فیلدی که منتظر کلیک روی ستون است
  const [mode,setMode]       = useState('all');
  const [fromDate,setFromDate]= useState(curYear()+'/01/01');
  const [toDate,setToDate]   = useState(curYear()+'/12/30');
  const [skipRows,setSkipRows]= useState(0);
  const [scale,setScale]     = useState(1);
  const [busy,setBusy]       = useState(false);
  const [error,setError]     = useState('');
  const [result,setResult]   = useState(null);
  const fileRef = useRef(null);

  const loadPreview = useCallback(async(f,sh)=>{
    setBusy(true); setError(''); setResult(null);
    try{
      const fd = new FormData();
      fd.append('file',f);
      if(sh) fd.append('sheet',sh);
      const r = await fetch('/api/income/import/preview/',{method:'POST',headers:authHeader(),body:fd});
      const d = await r.json();
      if(d.ok){
        setSheets(d.sheets); setSheet(d.sheet); setPreview(d.rows);
        setColCount(d.col_count); setTotalRows(d.total_rows);
      } else setError(d.error||'خطا در خواندن فایل');
    }catch(e){ setError('خطا در ارتباط با سرور'); }
    setBusy(false);
  },[]);

  const pickFile = e => {
    const f = e.target.files?.[0];
    if(!f) return;
    setFile(f); loadPreview(f,null);
  };

  const cellClick = colIdx => {
    if(!armed) return;
    setMap(p=>({...p,[armed]:colIdx+1}));   // اندیس ۱-پایه
    setArmed(null);
  };

  const doImport = async()=>{
    if(!file){ setError('فایلی انتخاب نشده است'); return; }
    const required = isBank ? 'amount_idx' : 'samp_amount_idx';
    if(!Number(map[required])){
      setError(isBank?'ستون مبلغ درآمد مشخص نشده است':'ستون مبلغ سمپ مشخص نشده است');
      return;
    }
    setBusy(true); setError(''); setResult(null);
    try{
      localStorage.setItem(storeKey,JSON.stringify(map));
      const cfg = {...map, sheet, mode, from_date:fromDate, to_date:toDate,
                   skip_rows:Number(skipRows)||0, ...(isBank?{scale:Number(scale)||1}:{})};
      const fd = new FormData();
      fd.append('file',file);
      fd.append('config',JSON.stringify(cfg));
      const r = await fetch(`/api/income/import/${isBank?'bank':'samp'}/`,
        {method:'POST',headers:authHeader(),body:fd});
      const d = await r.json();
      if(d.ok){ setResult(d); onDone?.(); }
      else setError(d.error||'خطا در ثبت اطلاعات');
    }catch(e){ setError('خطا در ارتباط با سرور'); }
    setBusy(false);
  };

  const accent = isBank ? '#e65100' : '#00838f';
  const accentGrad = isBank
    ? 'linear-gradient(135deg,#e65100,#f57c00)'
    : 'linear-gradient(135deg,#006064,#00838f)';

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={e=>e.stopPropagation()}>
        <div style={{...s.mHeader,background:accentGrad}}>
          <div style={s.mTitle}>
            {isBank?'🏦 دریافت ریز درآمد از فایل بانک':'💠 دریافت ریز درآمد از فایل سمپ'}
          </div>
          <button onClick={onClose} style={s.closeBtn}>✕</button>
        </div>

        <div style={s.mBody}>
          {error&&<div style={s.errBox}>{error}</div>}

          {/* ── انتخاب فایل ── */}
          <div style={s.section}>
            <div style={s.secTitle}>۱ — انتخاب مسیر</div>
            <div style={{display:'flex',gap:'9px',alignItems:'center',flexWrap:'wrap'}}>
              <input ref={fileRef} type="file" accept=".xlsx,.xlsm" onChange={pickFile} style={{display:'none'}}/>
              <button onClick={()=>fileRef.current?.click()} style={{...s.btn,background:accentGrad}}>
                📂 انتخاب فایل اکسل
              </button>
              <span style={{fontSize:'12px',color:'#555'}}>{file?file.name:'فایلی انتخاب نشده'}</span>
              {sheets.length>1&&(
                <select value={sheet} onChange={e=>{setSheet(e.target.value);loadPreview(file,e.target.value);}}
                  style={s.fI}>
                  {sheets.map(sh=><option key={sh} value={sh}>{sh}</option>)}
                </select>)}
              {totalRows>0&&<span style={s.pill}>{toFa(totalRows)} ردیف</span>}
            </div>
          </div>

          {preview.length>0&&<>
            {/* ── نگاشت ستون‌ها ── */}
            <div style={s.section}>
              <div style={s.secTitle}>۲ — نگاشت ستون‌ها</div>
              {armed&&<div style={s.blink}>👆 ستون مربوطه را در جدول زیر انتخاب کنید</div>}
              <div style={{display:'flex',gap:'9px',flexWrap:'wrap'}}>
                {FIELDS.map(f=>(
                  <div key={f.key} onClick={()=>setArmed(f.key)}
                    style={{...s.mapBox,borderColor:armed===f.key?accent:'#e0e0e0',
                            background:armed===f.key?'#fff8e1':'#fff'}}>
                    <div style={{fontSize:'10.5px',fontWeight:'700',color:'#555'}}>{f.label}</div>
                    <div style={{fontSize:'15px',fontWeight:'800',color:Number(map[f.key])?accent:'#bbb'}}>
                      {Number(map[f.key])?toFa(map[f.key]):'—'}
                    </div>
                  </div>))}
              </div>
            </div>

            {/* ── پیش‌نمایش ── */}
            <div style={s.section}>
              <div style={s.secTitle}>۳ — پیش‌نمایش (روی سلول کلیک کنید)</div>
              <div style={s.gridWrap}>
                <table style={{borderCollapse:'collapse',fontSize:'11px'}}>
                  <thead><tr>
                    {Array.from({length:colCount}).map((_,c)=>{
                      const used = FIELDS.find(f=>Number(map[f.key])===c+1);
                      return <th key={c} onClick={()=>cellClick(c)}
                        style={{...s.gTh,background:used?accent:'#1a2f50',cursor:armed?'pointer':'default'}}>
                        {toFa(c+1)}{used?<div style={{fontSize:'9px',fontWeight:'400'}}>{used.label.replace(' *','')}</div>:null}
                      </th>;})}
                  </tr></thead>
                  <tbody>
                    {preview.map((row,ri)=>(
                      <tr key={ri} style={{background:ri<Number(skipRows)?'#eeeeee':ri%2?'#fff5f5':'#fff'}}>
                        {Array.from({length:colCount}).map((_,c)=>(
                          <td key={c} onClick={()=>cellClick(c)}
                            style={{...s.gTd,cursor:armed?'pointer':'default',
                                    background:FIELDS.some(f=>Number(map[f.key])===c+1)?'#fff8e1':'transparent'}}>
                            {String(row[c]??'').slice(0,26)}
                          </td>))}
                      </tr>))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ── تنظیمات پردازش ── */}
            <div style={s.section}>
              <div style={s.secTitle}>۴ — تنظیمات پردازش</div>
              <div style={{display:'flex',gap:'16px',flexWrap:'wrap',alignItems:'flex-end'}}>
                <div>
                  <label style={s.radio}>
                    <input type="radio" checked={mode==='all'} onChange={()=>setMode('all')}/>
                    <span>همه ردیف‌ها پردازش شوند</span></label>
                  <label style={s.radio}>
                    <input type="radio" checked={mode==='range'} onChange={()=>setMode('range')}/>
                    <span>فقط ردیف‌های مربوط به بازه خاص</span></label>
                </div>
                {mode==='range'&&<>
                  <div><div style={s.fL}>از روز</div><JalaliDatePicker value={fromDate} onChange={setFromDate} placeholder="از روز"/></div>
                  <div><div style={s.fL}>تا</div><JalaliDatePicker value={toDate} onChange={setToDate} placeholder="تا روز"/></div>
                </>}
                <div><div style={s.fL}>حذف سطرهای ابتدایی (عنوان)</div>
                  <input type="number" min="0" value={skipRows} onChange={e=>setSkipRows(e.target.value)}
                    style={{...s.fI,width:'90px'}}/></div>
                {isBank&&(
                  <div><div style={s.fL}>مقیاس مبالغ</div>
                    <select value={scale} onChange={e=>setScale(e.target.value)} style={s.fI}>
                      {SCALES.map(o=><option key={o.val} value={o.val}>{o.label}</option>)}
                    </select></div>)}
              </div>
              {!isBank&&(
                <div style={s.note}>
                  ⚠️ اگر نوع درآمد یا واحد اجرایی موجود در فایل در اطلاعات پایه نباشد،
                  <b>به‌صورت خودکار ساخته می‌شود</b>.
                </div>)}
            </div>

            {/* ── نتیجه ── */}
            {result&&(
              <div style={s.resultBox}>
                <div style={{fontWeight:'800',marginBottom:'7px',fontSize:'13px'}}>✅ عملیات انجام شد</div>
                <div style={s.resGrid}>
                  <span>خوانده‌شده: <b>{numFmt(result.read)}</b></span>
                  <span>ثبت‌شده: <b style={{color:'#1b5e20'}}>{numFmt(result.inserted)}</b></span>
                  <span>تکراری (رد شد): <b style={{color:'#e65100'}}>{numFmt(result.duplicates)}</b></span>
                  {result.skipped_range>0&&<span>خارج از بازه: <b>{numFmt(result.skipped_range)}</b></span>}
                  {result.skipped>0&&<span>ناقص (رد شد): <b>{numFmt(result.skipped)}</b></span>}
                  {result.zero_amount>0&&<span>مبلغ صفر: <b>{numFmt(result.zero_amount)}</b></span>}
                  {result.new_incomes>0&&<span>درآمد جدید ساخته‌شده: <b style={{color:'#4a148c'}}>{numFmt(result.new_incomes)}</b></span>}
                  {result.new_departments>0&&<span>واحد جدید ساخته‌شده: <b style={{color:'#4a148c'}}>{numFmt(result.new_departments)}</b></span>}
                </div>
              </div>)}
          </>}
        </div>

        <div style={s.mFooter}>
          <button onClick={onClose} style={s.btnCancel}>{result?'بستن':'لغو'}</button>
          {preview.length>0&&!result&&(
            <button onClick={doImport} disabled={busy}
              style={{...s.btn,background:busy?'#bbb':accentGrad,padding:'8px 26px'}}>
              {busy?'⏳ لطفاً منتظر بمانید':'اعمال اطلاعات'}
            </button>)}
        </div>
      </div>
    </div>
  );
}

const s = {
  overlay:{position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',zIndex:1100,display:'flex',alignItems:'center',justifyContent:'center',padding:'14px'},
  modal:{background:'#fff',borderRadius:'16px',width:'100%',maxWidth:'1080px',maxHeight:'94vh',display:'flex',flexDirection:'column',boxShadow:'0 20px 60px rgba(0,0,0,0.4)',fontFamily:"'Vazirmatn',Tahoma,sans-serif",direction:'rtl'},
  mHeader:{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'14px 20px',borderRadius:'16px 16px 0 0'},
  mTitle:{fontSize:'15px',fontWeight:'700',color:'#fff'},
  closeBtn:{background:'rgba(255,255,255,0.15)',border:'1px solid rgba(255,255,255,0.3)',color:'#fff',width:'30px',height:'30px',borderRadius:'50%',cursor:'pointer',fontSize:'13px'},
  mBody:{overflowY:'auto',flex:1,padding:'16px 18px'},
  mFooter:{display:'flex',justifyContent:'flex-end',gap:'10px',padding:'12px 18px',borderTop:'1px solid #f0f2f7',background:'#f8f9fa',borderRadius:'0 0 16px 16px'},
  section:{marginBottom:'16px',paddingBottom:'13px',borderBottom:'1px solid #f0f2f7'},
  secTitle:{fontSize:'12.5px',fontWeight:'800',color:'#1a2f50',marginBottom:'9px'},
  mapBox:{border:'2px solid #e0e0e0',borderRadius:'9px',padding:'7px 12px',minWidth:'104px',textAlign:'center',cursor:'pointer',transition:'all .15s'},
  blink:{background:'#fff8e1',border:'1px solid #ffe082',borderRadius:'7px',padding:'6px 12px',color:'#e65100',fontSize:'12px',fontWeight:'700',marginBottom:'9px'},
  gridWrap:{overflow:'auto',maxHeight:'260px',border:'1px solid #e0e0e0',borderRadius:'8px'},
  gTh:{color:'#fff',padding:'5px 7px',fontSize:'11px',fontWeight:'700',position:'sticky',top:0,whiteSpace:'nowrap',minWidth:'70px'},
  gTd:{border:'1px solid #f0f0f0',padding:'4px 7px',whiteSpace:'nowrap',textAlign:'center',maxWidth:'170px',overflow:'hidden'},
  radio:{display:'flex',alignItems:'center',gap:'5px',fontSize:'12px',fontWeight:'600',color:'#444',cursor:'pointer',marginBottom:'4px'},
  fL:{fontSize:'11px',fontWeight:'600',color:'#555',marginBottom:'3px'},
  fI:{padding:'7px 11px',border:'1.5px solid #e0e0e0',borderRadius:'8px',fontSize:'13px',fontFamily:"'Vazirmatn',Tahoma,sans-serif",outline:'none',direction:'rtl',textAlign:'right'},
  pill:{padding:'4px 11px',background:'#1a2f50',color:'#C8A84B',borderRadius:'14px',fontSize:'11px',fontWeight:'700'},
  btn:{padding:'7px 18px',color:'#fff',border:'none',borderRadius:'8px',cursor:'pointer',fontFamily:'inherit',fontSize:'13px',fontWeight:'700'},
  btnCancel:{padding:'8px 18px',background:'#e0e0e0',color:'#333',border:'none',borderRadius:'8px',cursor:'pointer',fontFamily:'inherit',fontSize:'13px'},
  errBox:{background:'#fff3f3',border:'1px solid #ffcdd2',borderRadius:'8px',padding:'10px 14px',color:'#c62828',marginBottom:'11px',fontSize:'13px'},
  note:{background:'#fff8e1',border:'1px solid #ffe082',borderRadius:'8px',padding:'8px 13px',color:'#e65100',fontSize:'11.5px',marginTop:'10px'},
  resultBox:{background:'#e8f5e9',border:'1.5px solid #a5d6a7',borderRadius:'10px',padding:'12px 16px',color:'#1b5e20'},
  resGrid:{display:'flex',gap:'18px',flexWrap:'wrap',fontSize:'12px'},
};
